import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, Plus, Trash2, GripVertical, Power } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const CATS = [
  { key: 'drink', label: 'Drink' },
  { key: 'snack', label: 'Snack' },
  { key: 'extra', label: 'Extra' },
];

export default function ConcessionItemsManager() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('drink');
  const [imageUrl, setImageUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['pos-items-admin'],
    queryFn: () => base44.entities.PosItem.list('sort_order'),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['pos-items-admin'] });

  const add = async () => {
    if (!name.trim() || !price) {
      toast({ variant: 'destructive', description: 'Name and price are required.' });
      return;
    }
    setBusy(true);
    try {
      await base44.entities.PosItem.create({
        name: name.trim(),
        price: Number(price),
        category,
        image_url: imageUrl.trim(),
        active: true,
        sort_order: (items?.length || 0),
      });
      setName(''); setPrice(''); setImageUrl(''); setCategory('drink');
      toast({ description: 'Item added.' });
      refresh();
    } catch (e) {
      toast({ variant: 'destructive', description: e?.message || 'Could not add item.' });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (it) => {
    try {
      await base44.entities.PosItem.update(it.id, { active: !it.active });
      refresh();
    } catch (e) {
      toast({ variant: 'destructive', description: e?.message || 'Update failed.' });
    }
  };

  const remove = async (it) => {
    try {
      await base44.entities.PosItem.delete(it.id);
      refresh();
      toast({ description: 'Item removed.' });
    } catch (e) {
      toast({ variant: 'destructive', description: e?.message || 'Delete failed.' });
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-5 flex items-center gap-2">
        <Plus className="w-4 h-4 text-primary" />
        <p className="text-white/40 text-sm">Drinks, snacks and extras sold at the walk-in POS. Tap an item on the iPad to add it to a session order.</p>
      </div>

      {/* Add form */}
      <div className="bg-[#111115] border border-white/5 p-4 mb-6">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-3">ADD NEW ITEM</p>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
          <div className="sm:col-span-4">
            <label className="text-white/30 text-[10px] font-bold tracking-widest">NAME</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Energy Drink"
              className="mt-1 w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm px-3 py-2 outline-none" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-white/30 text-[10px] font-bold tracking-widest">PRICE £</label>
            <input type="number" min="0" step="0.10" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="2.50"
              className="mt-1 w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm px-3 py-2 outline-none" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-white/30 text-[10px] font-bold tracking-widest">CATEGORY</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm px-3 py-2 outline-none">
              {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-3">
            <label className="text-white/30 text-[10px] font-bold tracking-widest">IMAGE URL (optional)</label>
            <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…"
              className="mt-1 w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm px-3 py-2 outline-none" />
          </div>
          <div className="sm:col-span-1 flex items-end">
            <button onClick={add} disabled={busy} className="w-full bg-primary text-white font-bold text-xs tracking-widest py-2.5 disabled:opacity-40 flex items-center justify-center gap-1">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-white/40 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (items?.length || 0) === 0 ? (
        <p className="text-white/30 text-sm py-8 border border-dashed border-white/10 px-4">No concession items yet.</p>
      ) : (
        <div className="border border-white/5">
          <div className="grid grid-cols-12 px-4 py-2.5 bg-[#111115] border-b border-white/5">
            <p className="col-span-5 text-white/30 text-[10px] font-bold tracking-widest">ITEM</p>
            <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">CATEGORY</p>
            <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest text-right">PRICE</p>
            <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest text-right">ACTIONS</p>
          </div>
          {items.map((it) => (
            <div key={it.id} className={`grid grid-cols-12 px-4 py-3 border-b border-white/5 items-center ${it.active === false ? 'opacity-40' : ''}`}>
              <div className="col-span-5 flex items-center gap-2 min-w-0">
                <GripVertical className="w-3.5 h-3.5 text-white/15 shrink-0" />
                {it.image_url && <img src={it.image_url} alt="" className="w-7 h-7 object-cover shrink-0" />}
                <p className="text-white text-sm truncate">{it.name}</p>
              </div>
              <p className="col-span-2 text-white/50 text-xs uppercase">{it.category}</p>
              <p className="col-span-2 text-primary font-bold text-sm text-right">£{Number(it.price).toFixed(2)}</p>
              <div className="col-span-3 flex items-center justify-end gap-1.5">
                <button onClick={() => toggleActive(it)} title={it.active === false ? 'Show on POS' : 'Hide from POS'}
                  className="w-8 h-8 flex items-center justify-center border border-white/10 text-white/50 hover:text-white hover:border-white/30">
                  <Power className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(it)} className="w-8 h-8 flex items-center justify-center border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/30">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}